import { useState } from 'react'
import { IconAlarm, IconBell, IconChecks } from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'

import { notificationApi } from '@/api/endpoints'
import type { AppNotification } from '@/api/types'
import { formatRelative } from '@/utils/format'
import { Button, Popover, cx } from '@/ui'

/**
 * Üst çubuktaki zil: uygulama içi bildirimler.
 *
 * <p>Dakikada bir yoklanır (hatırlatıcı uyarıları sunucuda dakikalık işle üretilir);
 * sekme arka plandayken react-query yoklamayı durdurur.
 */
export function NotificationBell() {
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const qc = useQueryClient()

  const query = useQuery({
    queryKey: ['notifications'],
    queryFn: notificationApi.list,
    refetchInterval: 60_000,
    staleTime: 30_000,
  })
  const unread = query.data?.unread ?? 0

  const markRead = useMutation({
    mutationFn: notificationApi.markRead,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  })
  const markAll = useMutation({
    mutationFn: notificationApi.markAllRead,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  })

  const openItem = (n: AppNotification) => {
    if (!n.readAt) markRead.mutate(n.id)
    setOpen(false)
    if (n.link) navigate(n.link)
  }

  return (
    <Popover open={open} onOpenChange={setOpen} align="end" width={360} trigger={
      <button type="button" className="nx-topbar__icon nx-bell" aria-label={unread ? `Bildirimler, ${unread} okunmamış` : 'Bildirimler'}
        title="Bildirimler">
        <IconBell size={19} stroke={1.8} />
        {unread > 0 && <span className="nx-bell__badge nx-nums" aria-hidden="true">{unread > 99 ? '99+' : unread}</span>}
      </button>
    }>
      <div className="nx-bell__panel">
        <div className="nx-bell__head">
          <strong>Bildirimler</strong>
          {unread > 0 && (
            <Button size="sm" variant="ghost" leftIcon={<IconChecks size={15} stroke={1.8} aria-hidden="true" />}
              loading={markAll.isPending} onClick={() => markAll.mutate()}>
              Tümünü okundu say
            </Button>
          )}
        </div>
        {!query.data?.items.length ? (
          <p className="nx-bell__empty">{query.isLoading ? 'Yükleniyor…' : 'Bildirim yok.'}</p>
        ) : (
          <ul className="nx-bell__list">
            {query.data.items.map((n) => (
              <li key={n.id}>
                <button type="button" className={cx('nx-bell__item', !n.readAt && 'is-unread')} onClick={() => openItem(n)}>
                  <span className="nx-bell__icon" aria-hidden="true"><IconAlarm size={16} stroke={1.8} /></span>
                  <span className="nx-bell__text">
                    <span className="nx-bell__title">{n.title}</span>
                    {n.body && <span className="nx-bell__body">{n.body}</span>}
                    <span className="nx-bell__time">{formatRelative(n.createdAt)}</span>
                  </span>
                  {!n.readAt && <span className="nx-bell__dot" aria-label="okunmamış" />}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Popover>
  )
}
